import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { Switch, Tooltip, Typography } from '@app/ui';
import { InfoCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ToggleConfirmationModal from './ToggleConfirmationModal';
import { useRightsholderAttribution } from '../hooks/useRightsholderAttribution';
import usePendo from '@app/components/hooks/usePendo';
import sendNotification from '../../actions/notifications/send';
import { AttributionMode } from './types';
import connect from '../../utils/connect';
import combineActions from '../../utils/combineActions';
import { Notification } from '../notifications/Notifications';
import { Client, User } from '@app/types/api';
import styled from 'styled-components';

const StyledSwitch = styled(Switch)`
    background-color: #1668dc !important;

    &:hover {
        background-color: #009fdf !important;
        filter: brightness(1.1) !important;
    }

    &:focus {
        background-color: #009fdf !important;
    }

    &.ant-switch-checked {
        background-color: #009fdf !important;

        &:hover {
            background-color: #009fdf !important;
            filter: brightness(1.1) !important;
        }

        &:focus {
            background-color: #009fdf !important;
        }
    }

    &.ant-switch:not(.ant-switch-checked) {
        background-color: #009fdf !important;

        &:hover {
            background-color: #009fdf !important;
            filter: brightness(1.1) !important;
        }

        &:focus {
            background-color: #009fdf !important;
        }
    }
`;

type RhAttributionToggleProps = {
    sendNotification: (
        message: string,
        config?: Notification['options'],
    ) => void;
    isOnReportsPage: boolean;
    isOnAutomatedInsightsPage?: boolean;
    client?: Client;
    user?: User;
    isOnSmpPage?: boolean;
};

const getTargetMethodology = (checked: boolean): AttributionMode =>
    checked ? 'league' : 'team';

const PAGE_BY_PATH_SEGMENT: Record<string, string> = {
    '': 'home',
    insights: 'data_explorer',
    broadcasts: 'broadcast_exposure',
    'social-media-posts': 'social_media_exposure',
    reports: 'reports',
};

const getPageFromPathname = (pathname: string) => {
    const pathSegment = pathname.split('/')[3] ?? '';
    return PAGE_BY_PATH_SEGMENT[pathSegment] ?? 'other';
};

const RhAttributionToggle = ({
    sendNotification,
    isOnReportsPage,
    isOnAutomatedInsightsPage = false,
    client,
    user,
    isOnSmpPage = false,
}: RhAttributionToggleProps) => {
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const { pendo } = usePendo();
    const {
        rightsholderAttribution,
        updateRightsholderAttribution,
        isLoading,
    } = useRightsholderAttribution({ client });
    // A client with no stored mode is served team-attributed data, so the switch
    // has to render the team position for it. Treating null as "not team" would
    // show League while every query still ran in team mode.
    const effectiveAttribution: AttributionMode =
        rightsholderAttribution ?? 'team';
    const isTeamAttribution = effectiveAttribution === 'team';
    const [isChanging, setIsChanging] = useState(false);
    const [showConfirmation, setShowConfirmation] = useState(false);
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const [pendingChecked, setPendingChecked] = useState<boolean | null>(null);

    const getTrackPayload = (
        fromMethodology: AttributionMode,
        toMethodology: AttributionMode,
        extra: Record<string, unknown> = {},
    ) => ({
        client_id: client?.id,
        user_id: user?.id,
        user_email: user?.email,
        from_methodology: fromMethodology,
        to_methodology: toMethodology,
        page: getPageFromPathname(pathname),
        ...extra,
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const stored = localStorage.getItem('rh-attrib-dont-show');
            if (stored === 'true') {
                setDontShowAgain(true);
            }
        }
    }, []);

    const handleSwitchClick = (targetChecked: boolean) => {
        const fromMethodology: AttributionMode = effectiveAttribution;
        const toMethodology = getTargetMethodology(targetChecked);

        if (dontShowAgain) {
            handleConfirmChange(targetChecked, false);
            return;
        }

        setPendingChecked(targetChecked);
        setShowConfirmation(true);

        if (pendo) {
            pendo.track(
                'Relo Edge::RH Attribution::Toggle::Prompt Shown',
                getTrackPayload(fromMethodology, toMethodology),
            );
        }
    };

    // Only tracked while the modal is open; persisted when the user goes
    // through with the change. Ticking the box and then cancelling must not
    // suppress the prompt for a change that never happened.
    const [pendingDontShowAgain, setPendingDontShowAgain] = useState(false);

    const handleDontShowAgain = (checked: boolean) => {
        setPendingDontShowAgain(checked);
    };

    const handleConfirm = () => {
        // Use the position the user actually clicked. Re-deriving it by
        // inverting the current mode writes the opposite of their intent when
        // the mode changes between the click and the confirmation (another tab
        // updating it, for instance).
        const checked = pendingChecked ?? effectiveAttribution === 'team';
        if (pendingDontShowAgain) {
            setDontShowAgain(true);
            if (typeof window !== 'undefined') {
                localStorage.setItem('rh-attrib-dont-show', 'true');
            }
        }
        handleConfirmChange(checked, true);
        setShowConfirmation(false);
        setPendingChecked(null);
        setPendingDontShowAgain(false);
    };

    const handleConfirmChange = async (
        checked: boolean,
        promptShown: boolean,
    ) => {
        const fromMethodology: AttributionMode = effectiveAttribution;
        const toMethodology = getTargetMethodology(checked);
        setIsChanging(true);

        try {
            await updateRightsholderAttribution(toMethodology);

            if (pendo) {
                pendo.track(
                    'Relo Edge::RH Attribution::Toggle::Confirmed',
                    getTrackPayload(fromMethodology, toMethodology, {
                        prompt_shown: promptShown,
                    }),
                );
            }

            const capitalizedMode =
                toMethodology.charAt(0).toUpperCase() + toMethodology.slice(1);
            const previousMode =
                fromMethodology.charAt(0).toUpperCase() +
                fromMethodology.slice(1);
            sendNotification(
                t('rightsholderAttribution.notificationSwitched', {
                    mode: capitalizedMode,
                }),
                {
                    context: 'info',
                    description: t(
                        isOnSmpPage
                            ? 'rightsholderAttribution.notificationDescriptionSmpPage'
                            : 'rightsholderAttribution.notificationDescription',
                        {
                            previousMode: previousMode,
                        },
                    ),
                },
            );
        } catch (error) {
            sendNotification(error.message, { context: 'warning' });
        } finally {
            setIsChanging(false);
        }
    };

    const handleCancel = () => {
        const fromMethodology: AttributionMode = effectiveAttribution;
        let toMethodology: AttributionMode =
            fromMethodology === 'team' ? 'league' : 'team';
        if (pendingChecked != null) {
            toMethodology = getTargetMethodology(pendingChecked);
        }

        if (pendo) {
            pendo.track(
                'Relo Edge::RH Attribution::Toggle::Cancelled',
                getTrackPayload(fromMethodology, toMethodology),
            );
        }

        setShowConfirmation(false);
        setPendingChecked(null);
        setPendingDontShowAgain(false);
    };

    return (
        <div>
            <Typography.Text
                style={{
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 300,
                    paddingRight: '6px',
                }}
            >
                {t('rightsholderAttribution.teamAttributionLabel')}
            </Typography.Text>
            <StyledSwitch
                checked={!isTeamAttribution}
                onChange={(checked: boolean) => {
                    handleSwitchClick(checked);
                }}
                loading={isLoading || isChanging}
            />{' '}
            <Typography.Text
                style={{
                    fontSize: '14px',
                    fontFamily: 'Roboto, sans-serif',
                    fontWeight: 300,
                    paddingLeft: '6px',
                    paddingRight: '6px',
                }}
            >
                {t('rightsholderAttribution.leagueAttributionLabel')}
            </Typography.Text>
            <Tooltip
                title={
                    <div>
                        {t('rightsholderAttribution.tooltipDescription')}{' '}
                        <a
                            href="https://relometrics.freshdesk.com/support/solutions/articles/156000388343-rights-holder-attribution-in-edge-overview"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                                color: '#009FDF',
                                textDecoration: 'underline',
                            }}
                        >
                            {t('rightsholderAttribution.tooltipLinkText')}
                        </a>
                    </div>
                }
                placement="bottom"
            >
                <InfoCircleOutlined />
            </Tooltip>
            <ToggleConfirmationModal
                visible={showConfirmation}
                currentMode={effectiveAttribution}
                // The direction the user clicked, not the inverse of the mode
                // as it stands now: those differ if the mode changes while the
                // modal is open, and the copy has to describe what confirming
                // will actually do.
                targetMode={
                    pendingChecked != null
                        ? getTargetMethodology(pendingChecked)
                        : undefined
                }
                dontShowAgain={pendingDontShowAgain}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
                onDontShowAgain={handleDontShowAgain}
                isOnReportsPage={isOnReportsPage}
                isOnAutomatedInsightsPage={isOnAutomatedInsightsPage}
                isOnSmpPage={isOnSmpPage}
            />
        </div>
    );
};

export default connect(
    ['client', 'user'],
    combineActions({ sendNotification }),
)(RhAttributionToggle);
